import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import Button from '../../components/atoms/Button';
import TextField from '../../components/molecules/TextField';
import Icon, { IconSizes, IconVariants } from '../../components/foundations/Icon';
import GeneralPopup, { PopupTypes } from '../../components/organisms/GeneralPopup';
import { SmallDialog } from '../../components/widgets';
import {
    DEFAULT_SCHEDULE,
    MONITOR_TITLE_MAX_LENGTH,
    PROMPT_MAX_LENGTH,
    SCHEDULE_FREQUENCIES,
    TRANSLATION_KEY,
} from '../constants';
import { cronToPreset, presetToCron } from '../utils';
import SchedulePicker from './SchedulePicker';
import css from './MonitorDialog.css';

const DIALOG_KEY = `${TRANSLATION_KEY}.MONITOR_DIALOG`;

const formatCount = (length, max) => `${length.toLocaleString()}/${max.toLocaleString()}`;

const MonitorDialog = ({
    open,
    initialTitle = '',
    initialPrompt = '',
    initialCron,
    isNew = false,
    slackChannelName = null,
    onSave,
    onClose,
    onSuggestTitle,
    saving = false,
    error = null,
}) => {
    const [title, setTitle] = useState('');
    const [prompt, setPrompt] = useState('');
    const [preset, setPreset] = useState(DEFAULT_SCHEDULE);
    const [suggesting, setSuggesting] = useState(false);
    const [confirmingDiscard, setConfirmingDiscard] = useState(false);
    const suggestRequestId = useRef(0);

    useEffect(() => {
        if (!open) return;
        setTitle(initialTitle);
        setPrompt(initialPrompt);
        setPreset(cronToPreset(initialCron) || DEFAULT_SCHEDULE);
        setSuggesting(false);
        setConfirmingDiscard(false);
        suggestRequestId.current += 1;
    }, [open, initialTitle, initialPrompt, initialCron]);

    const suggest = async () => {
        if (suggesting || !prompt.trim()) return;
        suggestRequestId.current += 1;
        const requestId = suggestRequestId.current;
        setSuggesting(true);
        const suggested = await onSuggestTitle(prompt.trim());
        if (requestId !== suggestRequestId.current) return;
        setSuggesting(false);
        if (suggested) setTitle(suggested);
    };

    const cron = presetToCron(preset);
    const hasEdits = title !== initialTitle || prompt !== initialPrompt || cron !== initialCron;
    const saveDisabled =
        saving ||
        suggesting ||
        !prompt.trim() ||
        (!isNew && !hasEdits) ||
        (preset.frequency === SCHEDULE_FREQUENCIES.WEEKLY && !preset.days.length);

    const requestClose = () => (hasEdits ? setConfirmingDiscard(true) : onClose());

    return (
        <>
            <SmallDialog
                open={open}
                showX={false}
                onClose={requestClose}
                onEscapePress={requestClose}
                classes={{ paperScrollBody: css.dialog }}
            >
                <div className={css.header}>
                    <div className={css.heading}>
                        <Translate id={`${DIALOG_KEY}.${isNew ? 'TITLE_NEW' : 'TITLE'}`} />
                    </div>
                    <Icon name="x" size={IconSizes.SMALL} className={css.close} onClick={requestClose} />
                </div>

                <div className={css.body}>
                    <div className={css.block}>
                        <div className={css.blockHeader}>
                            <div className={css.label}>
                                <Translate id={`${DIALOG_KEY}.NAME_LABEL`} />
                            </div>
                            <Button
                                type="flat"
                                Icon={suggesting ? null : 'sparkles'}
                                iconSize={IconSizes.XX_SMALL}
                                iconVariant={IconVariants.ACTIVE}
                                className={css.suggestLink}
                                textClassName={css.suggestContent}
                                disabled={!prompt.trim() || suggesting}
                                onClick={suggest}
                            >
                                {suggesting && <span className={css.suggestSpinner} />}
                                <Translate id={`${DIALOG_KEY}.SUGGEST`} />
                            </Button>
                            <span className={css.counter}>{formatCount(title.length, MONITOR_TITLE_MAX_LENGTH)}</span>
                        </div>
                        <TextField
                            value={title}
                            onChange={setTitle}
                            disabled={suggesting}
                            placeholder={`${DIALOG_KEY}.NAME_PLACEHOLDER`}
                            maxLength={MONITOR_TITLE_MAX_LENGTH}
                            containerClass={css.nameField}
                            inputClassName={css.nameInput}
                        />
                    </div>

                    <div className={css.block}>
                        <div className={css.blockHeader}>
                            <div className={css.label}>
                                <Translate id={`${DIALOG_KEY}.INSTRUCTIONS_LABEL`} />
                            </div>
                            <span className={css.counter}>{formatCount(prompt.length, PROMPT_MAX_LENGTH)}</span>
                        </div>
                        <TextField
                            type="textarea"
                            value={prompt}
                            onChange={setPrompt}
                            placeholder={`${DIALOG_KEY}.INSTRUCTIONS_PLACEHOLDER`}
                            maxLength={PROMPT_MAX_LENGTH}
                            containerClass={css.promptField}
                            inputClassName={css.promptInput}
                        />
                    </div>

                    <SchedulePicker preset={preset} onChange={setPreset} slackChannelName={slackChannelName} />

                    {error && <div className={css.error}>{error}</div>}
                </div>

                <div className={css.footer}>
                    <Button type="flat" className={css.cancel} onClick={requestClose}>
                        <Translate id={`${DIALOG_KEY}.CANCEL`} />
                    </Button>
                    <Button
                        type="primary"
                        className={css.save}
                        disabled={saveDisabled}
                        showSpinner={saving}
                        onClick={() => onSave({ title: title.trim(), prompt: prompt.trim(), cron })}
                    >
                        <Translate id={`${DIALOG_KEY}.SAVE`} />
                    </Button>
                </div>
            </SmallDialog>

            <GeneralPopup
                open={confirmingDiscard}
                type={PopupTypes.WARNING_WITH_CANCEL}
                title={`${DIALOG_KEY}.DISCARD_TITLE`}
                text={`${DIALOG_KEY}.DISCARD_TEXT`}
                acceptText={`${DIALOG_KEY}.DISCARD_ACCEPT`}
                rejectText={`${DIALOG_KEY}.DISCARD_REJECT`}
                onAccept={() => {
                    setConfirmingDiscard(false);
                    onClose();
                }}
                onReject={() => setConfirmingDiscard(false)}
            />
        </>
    );
};

MonitorDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    initialTitle: PropTypes.string,
    initialPrompt: PropTypes.string,
    initialCron: PropTypes.string.isRequired,
    isNew: PropTypes.bool,
    slackChannelName: PropTypes.string,
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuggestTitle: PropTypes.func.isRequired,
    saving: PropTypes.bool,
    error: PropTypes.string,
};

export default MonitorDialog;
